import { Link } from "react-router-dom";
import { BadgeCheck, Users, Clapperboard, MessageSquare, Building2, Briefcase, CalendarDays, Map, Mail } from "lucide-react";
import { Logo } from "@/components/Brand";
import { SupportLink, WhatsAppSupport } from "@/components/WhatsAppSupport";
import { TAXMANDI_URL } from "@/lib/api";

const FOR_PROS = [
  "Create a verified professional profile", "Showcase your services, expertise, and specializations",
  "Share posts, reels, articles, and professional updates", "Get discovered by clients looking for CA, CS, CMA services",
  "Network with fellow professionals across India", "Find jobs, internships, and collaboration opportunities", "Book and manage consultations",
];
const FOR_CLIENTS = [
  "Discover verified CA, CS, CMA professionals near you", "View profiles, services, reviews, and ratings",
  "Connect directly via message, call, or WhatsApp", "Post your service requirements on the Requirements Board",
];
const FEATURES = [
  [BadgeCheck, "Professional verification with verified badge"], [Clapperboard, "Instagram-style feed and reels for knowledge sharing"],
  [MessageSquare, "Direct messaging and consultation booking"], [Building2, "Firm profiles and team discovery"],
  [Briefcase, "Jobs and opportunities board"], [CalendarDays, "Events and webinars"], [Map, "India-wide professional directory"],
];
const LEGAL = [["Privacy Policy", "privacy"], ["Terms of Service", "terms"], ["Account & Data Deletion", "data-deletion"]];

const List = ({ title, items, testid }) => (
  <div className="tm-card p-7" data-testid={testid}>
    <h2 className="font-display text-lg font-bold text-tm-green">{title}</h2>
    <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
      {items.map((t) => <li key={t} className="flex gap-2.5"><BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-tm-gold" />{t}</li>)}
    </ul>
  </div>
);

export default function About() {
  return (
    <div className="min-h-screen bg-tm-sand">
      <header className="border-b bg-white"><div className="mx-auto flex h-16 max-w-5xl items-center px-6"><Logo /></div></header>
      <main className="mx-auto max-w-5xl space-y-8 px-6 py-12" data-testid="about-page">
        <div className="tm-fade-up">
          <p className="tm-eyebrow">About us</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-tm-green sm:text-5xl">India's professional network for CA, CS & CMA experts.</h1>
          <p className="mt-5 max-w-3xl text-base text-slate-700 md:text-lg">Taxmandi is India's first dedicated professional social networking platform built exclusively for Chartered Accountants, Company Secretaries, Cost & Management Accountants, finance professionals, and accounting firms.</p>
          <p className="mt-3 max-w-3xl text-slate-600">Whether you want to build your personal brand, share knowledge, get discovered by clients, or find the right professional — Taxmandi is your platform.</p>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <List title="For Professionals" items={FOR_PROS} testid="about-for-pros" />
          <List title="For Clients and Businesses" items={FOR_CLIENTS} testid="about-for-clients" />
        </div>
        <div className="rounded-3xl bg-tm-green p-8 text-white" data-testid="about-features">
          <h2 className="font-display text-xl font-bold">Key Features</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(([Icon, t]) => <div key={t} className="flex items-start gap-3 rounded-2xl bg-white/10 p-4 text-sm"><Icon className="h-5 w-5 shrink-0 text-tm-gold" />{t}</div>)}
          </div>
          <p className="mt-8 font-display text-2xl font-extrabold">Connect. Learn. Grow.</p>
          <p className="text-white/70">Taxmandi — Where Professionals Meet.</p>
        </div>
        <div className="tm-card grid gap-6 p-7 md:grid-cols-2" data-testid="about-contact">
          <div>
            <h2 className="font-display text-lg font-bold text-tm-green">Contact us</h2>
            <div className="mt-3 space-y-2 text-sm">
              <SupportLink className="block font-semibold text-[#1ea952] hover:underline" />
              <a href="mailto:taxmandiapp@gmail.com" className="flex items-center gap-2 text-slate-700 hover:underline" data-testid="about-email-link"><Mail className="h-4 w-4" />taxmandiapp@gmail.com</a>
              <p className="flex items-center gap-2 text-slate-700"><Users className="h-4 w-4" />LIFEBELONGS PRIVATE LIMITED</p>
            </div>
          </div>
          <div>
            <h2 className="font-display text-lg font-bold text-tm-green">Policies</h2>
            <div className="mt-3 space-y-2 text-sm">
              {LEGAL.map(([l, p]) => <a key={p} href={`${TAXMANDI_URL}/api/legal/${p}`} target="_blank" rel="noreferrer" className="block text-tm-green-light hover:underline" data-testid={`about-legal-${p}`}>{l}</a>)}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/register" className="rounded-full bg-tm-green px-6 py-3 text-sm font-semibold text-white" data-testid="about-join-button">Join Taxmandi</Link>
          <a href="https://play.google.com/store/apps/details?id=com.taxmandi.app" target="_blank" rel="noreferrer" className="rounded-full border border-tm-green px-6 py-3 text-sm font-semibold text-tm-green" data-testid="about-play-store-link">Get the Android app</a>
        </div>
      </main>
      <WhatsAppSupport />
    </div>
  );
}
